(() => {
  'use strict';

  window.OssMotion?.destroy();

  const gsap = window.gsap;
  if (!gsap || !window.IntersectionObserver || !window.matchMedia) return;

  const selector = '[data-oss-reveal]';
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const pending = new Map();
  let destroyed = false;

  const settle = (element) => {
    const record = pending.get(element);
    if (!record) return;

    observer.unobserve(element);
    pending.delete(element);
    seen.add(element);

    try {
      record.context?.revert();
    } catch {
      record.tween?.kill();
      if (record.transform) {
        element.style.setProperty('transform', record.transform, record.transformPriority);
      } else {
        element.style.removeProperty('transform');
      }
    }
  };

  const reveal = (element) => {
    const record = pending.get(element);
    if (!record) return;

    observer.unobserve(element);
    if (preference.matches || document.hidden || !element.isConnected || element.contains(document.activeElement)) {
      settle(element);
      return;
    }

    try {
      record.context = gsap.context(() => {}, element);
      record.context.add(() => {
        record.tween = gsap.from(element, {
          y: 12,
          duration: 0.48,
          ease: 'power2.out',
          force3D: false,
          onComplete: () => settle(element),
        });
      });
    } catch {
      settle(element);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { threshold: 0, rootMargin: '0px 0px 24px 0px' }
  );

  const initialize = (root = document) => {
    if (destroyed || preference.matches) return;

    const elements = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) elements.unshift(root);

    for (const element of elements) {
      if (seen.has(element) || pending.has(element)) continue;
      if (element.parentElement?.closest(selector)) continue;

      const bounds = element.getBoundingClientRect();
      if (bounds.top < window.innerHeight || element.contains(document.activeElement)) {
        seen.add(element);
        continue;
      }

      pending.set(element, {
        context: null,
        tween: null,
        transform: element.style.getPropertyValue('transform'),
        transformPriority: element.style.getPropertyPriority('transform'),
      });
      observer.observe(element);
    }
  };

  const settleWithin = (root) => {
    for (const element of pending.keys()) {
      if (root === element || root.contains(element)) settle(element);
    }
  };

  const settleAll = () => {
    for (const element of pending.keys()) settle(element);
  };

  const onSectionLoad = (event) => initialize(event.target);
  const onSectionUnload = (event) => settleWithin(event.target);
  const onSectionSelect = (event) => settleWithin(event.target);
  const onFocus = (event) => {
    for (const element of pending.keys()) {
      if (element === event.target || element.contains(event.target)) settle(element);
    }
  };
  const onPreferenceChange = () => {
    settleAll();
    observer.disconnect();
    if (!preference.matches) initialize();
  };
  const onPageHide = () => {
    settleAll();
    observer.disconnect();
  };
  const onPageShow = () => initialize();
  const onReady = () => initialize();

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);
  document.addEventListener('focusin', onFocus);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  preference.addEventListener('change', onPreferenceChange);

  window.OssMotion = {
    destroy() {
      destroyed = true;
      settleAll();
      observer.disconnect();
      document.removeEventListener('DOMContentLoaded', onReady);
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('focusin', onFocus);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      preference.removeEventListener('change', onPreferenceChange);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    initialize();
  }
})();
